/* eslint-disable */
/* AtlasLandRuntime: draws a styled `land_map` config (meta.version >= 1) on a Mapbox GL map.
 * One file, two renderers: inlined into the headless PNG/PDF card (viz_print.build_land_card_html) and copied
 * verbatim into the client (src/components/atlas/maps/atlasLandRuntime.js). Change this copy, then re-copy.
 * No mapbox-gl import: the caller owns the map (and the popup in chat).
 *
 * Layer kinds (config.layers[], drawn in array order; the server sorts them so the lot outline and markers stay on top):
 *   polygon  -> fill [+ casing] + line (+ dashed line for features crossing the lot line)
 *   line     -> line (dashed when style.dash)
 *   cells    -> the parcel's elevation / slope grid: fill, or fill-extrusion bars when style.extrude
 *   contours -> casing + line + labels along the line
 *   point    -> circle (hollow rings when feature.hollow) + labels for features with a label
 * camera: {pitch, bearing, terrain_3d, exaggeration, buildings_3d}; subject: {lat, lng, label}.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.AtlasLandRuntime = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var BASEMAPS = {
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
    streets: 'mapbox://styles/mapbox/streets-v12',
    light: 'mapbox://styles/mapbox/light-v11',
  };
  var DEM_SOURCE = 'atlas-land-dem';
  var SUBJECT = '#E26313';
  var PREFIX = 'land-';
  var MAX_ZOOM = 19;
  var LEGACY_KIND = { swatch: 'area', line: 'line', point: 'point' };

  function num(v, d) { var n = Number(v); return v === null || v === undefined || v === '' || !isFinite(n) ? d : n; }
  function isArr(v) { return Array.isArray(v) && v.length > 0; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function hasMeta(cfg) { return !!(cfg && cfg.type === 'land_map' && cfg.meta && Number(cfg.meta.version) >= 1); }

  function basemapStyle(cfg) {
    var name = String((cfg && cfg.basemap) || '').toLowerCase();
    return BASEMAPS[name] || BASEMAPS.light;
  }

  function cam(cfg) {
    var c = (cfg && cfg.camera) || {};
    return { pitch: Math.max(0, Math.min(80, num(c.pitch, 0))), bearing: num(c.bearing, 0), terrain3d: !!c.terrain_3d, exaggeration: Math.max(1, Math.min(4, num(c.exaggeration, 1.5))),
             buildings3d: !!c.buildings_3d };
  }

  function firstSymbolId(map) {
    try { return (map.getStyle().layers.find(function (l) { return l.type === 'symbol'; }) || {}).id; } catch (e) { return undefined; }
  }

  /** Quiet the basemap so overlays read: dim and desaturate satellite imagery; drop POI clutter on light/streets. */
  function prepareBasemap(map, cfg) {
    var layers = [];
    try { layers = map.getStyle().layers || []; } catch (e) { layers = []; }
    var sat = String(cfg && cfg.basemap || '') === 'satellite';
    layers.forEach(function (l) {
      try {
        if (sat && l.type === 'raster') {
          map.setPaintProperty(l.id, 'raster-saturation', -0.35);
          map.setPaintProperty(l.id, 'raster-brightness-max', 0.82);
          map.setPaintProperty(l.id, 'raster-contrast', -0.06);
        }
        if (l.type === 'symbol' && /poi|transit|airport/.test(l.id)) map.setLayoutProperty(l.id, 'visibility', 'none');
      } catch (e) { /* layer vanished during a style swap */ }
    });
  }

  /** 3D terrain (Mapbox DEM; only needs the Mapbox token) and a soft horizon for tilted views. */
  function addTerrain(map, cfg) {
    var c = cam(cfg);
    if (c.terrain3d) {
      if (!map.getSource(DEM_SOURCE)) map.addSource(DEM_SOURCE, { type: 'raster-dem', url: 'mapbox://mapbox.mapbox-terrain-dem-v1', tileSize: 512, maxzoom: 14 });
      map.setTerrain({ source: DEM_SOURCE, exaggeration: c.exaggeration });
    }
    if (c.pitch > 0 && typeof map.setFog === 'function') {
      try { map.setFog({ range: [1.2, 9], color: '#f3f4f6', 'horizon-blend': 0.08, 'high-color': '#dbe7f5', 'space-color': '#dbe7f5', 'star-intensity': 0 }); } catch (e) { /* older GL */ }
    }
  }

  function fc(geojson) {
    if (!geojson) return { type: 'FeatureCollection', features: [] };
    if (geojson.type === 'FeatureCollection') return { type: 'FeatureCollection', features: geojson.features || [] };
    if (geojson.type === 'Feature') return { type: 'FeatureCollection', features: [geojson] };
    return { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: geojson }] };
  }

  function popupProps(popup, fallback) {
    var out = {};
    if (popup && popup.title) out.popup_title = String(popup.title);
    if (popup && isArr(popup.lines)) out.popup_lines = JSON.stringify(popup.lines.filter(function (l) { return l; }).map(String));
    if (!out.popup_title && fallback) out.popup_title = String(fallback);
    return out;
  }

  function pointFC(layer) {
    var feats = (layer.features || []).map(function (p) {
      var lng = Number(p.lng), lat = Number(p.lat);
      if (!isFinite(lng) || !isFinite(lat)) return null;
      var props = Object.assign({ label: p.label == null ? '' : String(p.label), color: p.color || '#2a78d6', radius: num(p.radius, 6),
                                  hollow: !!p.hollow, highlight: !!p.highlight }, popupProps(p.popup, p.label));
      return { type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: props };
    }).filter(Boolean);
    return { type: 'FeatureCollection', features: feats };
  }

  function polygonFC(layer) {
    var data = fc(layer.geojson);
    data.features = data.features.map(function (f) {
      var props = Object.assign({}, f.properties || {});
      if (props.elev_ft != null && !props.popup) {
        // terrain cells carry values, not text: build the hover card here so the stored grid stays small
        props.popup = { title: Number(props.elev_ft).toFixed(1) + ' ft', lines: [props.slope_pct != null ? 'slope ' + Number(props.slope_pct).toFixed(1) + '%' + (props.band ? ' (' + String(props.band).toLowerCase() + ')' : '') : ''] };
      }
      Object.assign(props, popupProps(props.popup, props.name));
      delete props.popup;
      return Object.assign({}, f, { properties: props });
    });
    return data;
  }

  var IS_CROSSING = ['==', ['get', 'crosses_boundary'], true];
  var IS_SURROUND = ['==', ['get', 'location'], 'surroundings'];

  function fillOpacity(st) {
    var base = num(st.fill_opacity, 0.3), sur = num(st.fill_opacity_surround, null), cross = num(st.fill_opacity_crossing, null);
    if (sur === null && cross === null) return base;
    return ['case', IS_CROSSING, cross !== null ? cross : (sur !== null ? sur : base), IS_SURROUND, sur !== null ? sur : base, base];
  }
  function lineOpacity(st) {
    var base = num(st.line_opacity, 1), sur = num(st.line_opacity_surround, null);
    return sur === null ? base : ['case', IS_SURROUND, sur, base];
  }

  /** Add every layer of the config. Returns {ids, hoverable}. options: {beforeId, print}. */
  function addLayers(map, cfg, options) {
    var opts = options || {};
    var before = opts.beforeId === undefined ? firstSymbolId(map) : opts.beforeId;
    var ids = [], hoverable = [];
    var seen = {};
    (cfg.layers || []).forEach(function (layer, i) {
      if (!layer) return;
      var key = String(layer.id || ('layer' + i));
      while (seen[key]) key += '_' + i;
      seen[key] = true;
      var src = PREFIX + key;
      var st = layer.style || {};
      var kind = layer.kind;
      if (map.getSource(src)) return;
      if (kind === 'point') {
        map.addSource(src, { type: 'geojson', data: pointFC(layer) });
        map.addLayer({ id: src + '-circle', type: 'circle', source: src, paint: {
          'circle-color': ['case', ['get', 'hollow'], '#ffffff', ['get', 'color']],
          'circle-radius': ['to-number', ['get', 'radius']],
          'circle-opacity': ['case', ['get', 'hollow'], 0.85, 0.95],
          'circle-stroke-color': ['case', ['get', 'hollow'], ['get', 'color'], st.stroke || '#ffffff'],
          'circle-stroke-width': ['case', ['get', 'highlight'], 3, ['get', 'hollow'], 2, num(st.stroke_width, 1.5)],
          // markers face the camera on tilted views (drawn flat they squash into ellipses)
          'circle-pitch-alignment': cam(cfg).pitch > 0 ? 'viewport' : 'map',
        } });
        ids.push(src + '-circle'); hoverable.push(src + '-circle');
        if (st.show_labels !== false) {
          map.addLayer({ id: src + '-label', type: 'symbol', source: src, filter: ['!=', ['get', 'label'], ''], layout: {
            'text-field': ['get', 'label'], 'text-size': opts.print ? 11 : 11.5, 'text-offset': [0, 1.0], 'text-anchor': 'top', 'text-optional': true,
            'text-max-width': 9, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
          }, paint: { 'text-color': st.label_color || '#111827', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 } });
          ids.push(src + '-label');
        }
        return;
      }
      map.addSource(src, { type: 'geojson', data: polygonFC(layer) });
      if (kind === 'cells') {
        if (st.extrude) {
          map.addLayer({ id: src + '-bars', type: 'fill-extrusion', source: src, paint: {
            'fill-extrusion-color': ['get', 'color'], 'fill-extrusion-height': ['to-number', ['get', 'height']], 'fill-extrusion-base': 0,
            'fill-extrusion-opacity': num(st.opacity, 0.92), 'fill-extrusion-vertical-gradient': true,
          } }, before);
          ids.push(src + '-bars'); hoverable.push(src + '-bars');
        } else {
          map.addLayer({ id: src + '-fill', type: 'fill', source: src, paint: { 'fill-color': ['get', 'color'], 'fill-opacity': num(st.opacity, 0.72), 'fill-antialias': false } }, before);
          ids.push(src + '-fill'); hoverable.push(src + '-fill');
        }
        return;
      }
      if (kind === 'contours') {
        map.addLayer({ id: src + '-casing', type: 'line', source: src, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: {
          'line-color': st.casing || '#1f2937', 'line-opacity': num(st.casing_opacity, 0.55),
          'line-width': ['case', ['get', 'index'], num(st.index_width, 2) + 2, num(st.width, 1.1) + 1.6] } }, before);
        map.addLayer({ id: src + '-line', type: 'line', source: src, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: {
          'line-color': ['case', ['get', 'index'], st.index_line || '#ffffff', st.line || '#ffffff'], 'line-opacity': 0.95,
          'line-width': ['case', ['get', 'index'], num(st.index_width, 2), num(st.width, 1.1)] } }, before);
        map.addLayer({ id: src + '-label', type: 'symbol', source: src, filter: ['!=', ['get', 'label'], ''], layout: {
          'symbol-placement': 'line', 'text-field': ['get', 'label'], 'text-size': 11, 'symbol-spacing': 90, 'text-keep-upright': true, 'text-max-angle': 50,
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'] },
          paint: { 'text-color': st.label_color || '#111827', 'text-halo-color': '#ffffff', 'text-halo-width': 1.6 } });
        ids.push(src + '-casing', src + '-line', src + '-label');
        return;
      }
      if (kind === 'line') {
        var lp = { 'line-color': st.line || '#333333', 'line-width': num(st.width, 2), 'line-opacity': num(st.opacity, 0.9) };
        if (isArr(st.dash)) lp['line-dasharray'] = st.dash;
        map.addLayer({ id: src + '-line', type: 'line', source: src, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: lp }, before);
        ids.push(src + '-line');
        return;
      }
      // polygon
      map.addLayer({ id: src + '-fill', type: 'fill', source: src, paint: { 'fill-color': st.fill || st.line || '#888888', 'fill-opacity': fillOpacity(st) } }, before);
      ids.push(src + '-fill'); hoverable.push(src + '-fill');
      if (st.casing) {
        map.addLayer({ id: src + '-casing', type: 'line', source: src, layout: { 'line-join': 'round' }, paint: {
          'line-color': st.casing, 'line-width': num(st.casing_width, num(st.width, 1) + 2.5), 'line-opacity': num(st.casing_opacity, 0.55) } }, before);
        ids.push(src + '-casing');
      }
      var paint = { 'line-color': st.line || st.fill || '#888888', 'line-width': num(st.width, 1), 'line-opacity': lineOpacity(st) };
      if (isArr(st.dash)) paint['line-dasharray'] = st.dash;
      var dashCross = isArr(st.dash_crossing);
      map.addLayer(Object.assign({ id: src + '-line', type: 'line', source: src, layout: { 'line-join': 'round' }, paint: paint },
                                 dashCross ? { filter: ['!=', ['get', 'crosses_boundary'], true] } : {}), before);
      ids.push(src + '-line');
      if (dashCross) {
        map.addLayer({ id: src + '-line-crossing', type: 'line', source: src, filter: IS_CROSSING, layout: { 'line-join': 'round' },
                       paint: Object.assign({}, paint, { 'line-dasharray': st.dash_crossing }) }, before);
        ids.push(src + '-line-crossing');
      }
    });
    addSubject(map, cfg, ids, opts);
    addBuildings3d(map, cfg, before, ids);
    return { ids: ids, hoverable: hoverable };
  }

  function hasBars(cfg) {
    return (cfg.layers || []).some(function (l) { return l && l.kind === 'cells' && l.style && l.style.extrude; });
  }

  /** A pin marks the lot on neighbourhood maps and tilted views; on bar views the bars are the lot. */
  function showSubjectPin(cfg) {
    var c = cam(cfg);
    return !!(cfg && cfg.subject && isFinite(Number(cfg.subject.lat)) && !hasBars(cfg) && (cfg.view === 'neighborhood' || c.pitch > 0));
  }

  function addSubject(map, cfg, ids, opts) {
    if (!showSubjectPin(cfg)) return;
    var s = cfg.subject;
    map.addSource(PREFIX + 'subject', { type: 'geojson', data: { type: 'FeatureCollection', features: [
      { type: 'Feature', geometry: { type: 'Point', coordinates: [Number(s.lng), Number(s.lat)] }, properties: { label: s.label || '' } }] } });
    // Tilted and ground views shrink anything drawn flat on the map, so the pin faces the camera there and grows.
    var tilted = cam(cfg).pitch > 0;
    var align = tilted ? 'viewport' : 'map';
    map.addLayer({ id: PREFIX + 'subject-halo', type: 'circle', source: PREFIX + 'subject', paint: { 'circle-radius': tilted ? 15 : 12, 'circle-color': SUBJECT, 'circle-opacity': 0.24, 'circle-pitch-alignment': align } });
    map.addLayer({ id: PREFIX + 'subject-dot', type: 'circle', source: PREFIX + 'subject', paint: { 'circle-radius': tilted ? 8 : 6.5, 'circle-color': SUBJECT, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2.2, 'circle-pitch-alignment': align } });
    if (cfg.view === 'neighborhood' || tilted) {
      map.addLayer({ id: PREFIX + 'subject-label', type: 'symbol', source: PREFIX + 'subject', layout: { 'text-field': ['get', 'label'], 'text-size': 12, 'text-offset': [0, 1.2],
        'text-anchor': 'top', 'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'] }, paint: { 'text-color': '#111827', 'text-halo-color': '#ffffff', 'text-halo-width': 1.8 } });
      ids.push(PREFIX + 'subject-label');
    }
    ids.push(PREFIX + 'subject-halo', PREFIX + 'subject-dot');
  }

  /** Extruded neighbourhood buildings from the basemap's own building layer (streets/light styles carry one). */
  function addBuildings3d(map, cfg, before, ids) {
    if (!cam(cfg).buildings3d) return;
    try {
      if (!map.getSource('composite')) return;
      map.addLayer({ id: PREFIX + 'buildings-3d', type: 'fill-extrusion', source: 'composite', 'source-layer': 'building', minzoom: 14.5, filter: ['==', ['get', 'extrude'], 'true'],
        paint: { 'fill-extrusion-color': '#d4d4d8', 'fill-extrusion-height': ['get', 'height'], 'fill-extrusion-base': ['get', 'min_height'], 'fill-extrusion-opacity': 0.75 } }, before);
      ids.push(PREFIX + 'buildings-3d');
    } catch (e) { /* style without a building layer */ }
  }

  function normalizeBounds(b) {
    if (!Array.isArray(b) || b.length !== 2 || !Array.isArray(b[0]) || !Array.isArray(b[1])) return null;
    var v = [Number(b[0][0]), Number(b[0][1]), Number(b[1][0]), Number(b[1][1])];
    if (!v.every(isFinite)) return null;
    return [[Math.min(v[0], v[2]), Math.min(v[1], v[3])], [Math.max(v[0], v[2]), Math.max(v[1], v[3])]];
  }

  /** Put the camera on the config's bounds at its pitch / bearing (tilted and ground views move a little closer). */
  function fit(map, cfg, options) {
    var opts = options || {};
    var c = cam(cfg);
    var pad = num(opts.padding, 36);
    var b = normalizeBounds(cfg.bounds);
    if (!b) {
      if (Array.isArray(cfg.center)) map.jumpTo({ center: cfg.center, zoom: num(cfg.zoom, 16), pitch: c.pitch, bearing: c.bearing });
      return;
    }
    var camOpts = null;
    try { camOpts = map.cameraForBounds(b, { padding: pad, bearing: c.bearing, pitch: c.pitch, maxZoom: MAX_ZOOM }); } catch (e) { camOpts = null; }
    if (!camOpts) { map.fitBounds(b, { padding: pad, duration: 0, maxZoom: MAX_ZOOM }); return; }
    var zoom = Math.min(MAX_ZOOM, num(camOpts.zoom, 16) + (c.pitch >= 70 ? 0.55 : c.pitch >= 40 ? 0.2 : 0));
    map.jumpTo({ center: camOpts.center, zoom: zoom, pitch: c.pitch, bearing: c.bearing });
  }

  /** The legend strip model (same shape AtlasMapLegend and the print card draw). */
  function legendModel(cfg) {
    var items = (cfg.legend || []).map(function (e) {
      return e && e.label ? { kind: e.kind || LEGACY_KIND[e.type] || 'area', label: String(e.label), color: e.color || null, count: e.count == null ? null : e.count } : null;
    }).filter(Boolean);
    if (showSubjectPin(cfg)) items.push({ kind: 'subject', label: cfg.subject.label || 'Subject', color: SUBJECT });
    (cfg.legend_notes || []).forEach(function (n) { if (n) items.push({ kind: 'note', label: String(n) }); });
    var st = cfg.legend_steps;
    var steps = st && isArr(st.colors) ? { colors: st.colors, labels: st.labels || [], note: st.note || '' } : null;
    return { title: steps ? (st.title || null) : null, steps: steps, categories: [], items: items };
  }

  function hoverHtml(props) {
    var p = props || {};
    var lines = [];
    try { lines = typeof p.popup_lines === 'string' ? JSON.parse(p.popup_lines) : (p.popup_lines || []); } catch (e) { lines = []; }
    var title = p.popup_title || p.label || '';
    if (!title && !lines.length) return '';
    return (title ? '<div><strong>' + esc(title) + '</strong></div>' : '') + lines.map(function (l) { return '<div>' + esc(l) + '</div>'; }).join('');
  }

  /** Everything after the style loads, in order. Returns the addLayers result. */
  function draw(map, cfg, options) {
    prepareBasemap(map, cfg);
    addTerrain(map, cfg);
    var out = addLayers(map, cfg, options);
    fit(map, cfg, options);
    return out;
  }

  return { BASEMAPS: BASEMAPS, hasMeta: hasMeta, basemapStyle: basemapStyle, camera: cam, prepareBasemap: prepareBasemap, addTerrain: addTerrain, addLayers: addLayers,
           fit: fit, legendModel: legendModel, hoverHtml: hoverHtml, draw: draw, showSubjectPin: showSubjectPin, normalizeBounds: normalizeBounds };
});
